import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import config from 'constants/config'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import ModalContext from 'contexts/ModalContext'
import Group from 'components/utils/Group'
import useSwitchRegion from 'hooks/useSwitchRegion'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { FlightsClassTypes } from 'constants/flight'
import { selectLoggedIn } from 'selectors/accountSelectors'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import TextLink from 'LuxKit/TextLink'
import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import LoyaltyJoinModal from 'loyalty/components/LoyaltyJoinModal'
import LoyaltySwitchRegionPrompt from 'loyalty/components/LoyaltySwitchRegionPrompt'
import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useLoyaltyAccount from 'loyalty/hooks/useLoyaltyAccount'
import useLoyaltyPurchasePointsOffers from 'loyalty/hooks/useLoyaltyPurchasePointsOffers'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import useMaldivesRewardsPayCalculatorPricing, {
  MALDIVES_REWARDS_PAY_CALCULATOR_CABINS,
  type MaldivesRewardsPayCalculatorCabin,
} from 'loyalty/hooks/useMaldivesRewardsPayCalculatorPricing'
import { getVisibleMaldivesRewardsPayCalculatorCabins } from 'loyalty/lib/maldivesRewardsPayCalculator/computeMaldivesRewardsPayCalculator'
import LoyaltyRewardsPayCalculatorCard from './LoyaltyRewardsPayCalculatorCard'

const ControlRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`

const BalanceChip = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  padding: ${rem(8)} ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.M};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: ${props => props.theme.shadow.flat.small};
`

interface Props {
  /** When false, show catalog “From $X + Y pts/person” instead of balance-aware calculation. */
  applyBalance?: boolean
}

function LoyaltyRewardsPayCalculatorSection({ applyBalance = true }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const isInLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const loyaltyAccountRegion = useAppSelector(getLoyaltyAccountRegion)
  const loyaltyAccount = useLoyaltyAccount()
  const [tier] = useCurrentLoyaltyTier()
  const { offers, fetching: offersFetching } = useLoyaltyPurchasePointsOffers()
  const { tierPricingByCabin, fetching: pricingFetching } = useMaldivesRewardsPayCalculatorPricing()

  const balanceFetching = isLoggedIn && (loyaltyAccount.fetching || loyaltyAccount.initial)
  const displayTier = loyaltyAccount.data?.tier ?? tier

  const [selectedCabin, setSelectedCabin] = useState<MaldivesRewardsPayCalculatorCabin>(
    FlightsClassTypes.ECONOMY,
  )

  const onCabinChange = useCallback((value: MaldivesRewardsPayCalculatorCabin) => {
    setSelectedCabin(value)
  }, [])

  const handleLoginClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const loyaltyCurrencyLabel = useMemo(
    () => config.regions.find(region => region.code === loyaltyAccountRegion)?.currencyCode,
    [loyaltyAccountRegion],
  )
  const [switchRegionPath, handleSwitchRegion] = useSwitchRegion(loyaltyAccountRegion)
  const needsEnroll = isLoggedIn && !hasLoyaltyAccount
  const wrongRegion = isLoggedIn && hasLoyaltyAccount && !isInLoyaltyRegion

  const balancePP = applyBalance && isLoggedIn && hasLoyaltyAccount && isInLoyaltyRegion
    ? (loyaltyAccount.data?.points ?? 0)
    : 0

  const activateMembershipLabel = intl.formatMessage({
    id: 'T9UHtgcdpQXkdUjc8gMK',
    defaultMessage: 'Activate membership',
    description: 'Primary button text on the loyalty join modal when a user clicks Join Société from the Maldives charter calendar.',
  })

  const handleEnrollClick = useCallback(async() => {
    const joinConfirmed = await showModal<boolean>(<LoyaltyJoinModal
      onAfterClose={() => {}}
      primaryActionCta={activateMembershipLabel}
    />)
    if (joinConfirmed) {
      dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
    }
  }, [showModal, activateMembershipLabel, dispatch])

  const isCalculatorReady = !pricingFetching && (!applyBalance || !offersFetching)

  const visibleCabins = useMemo(() => {
    if (!isCalculatorReady) {
      return [...MALDIVES_REWARDS_PAY_CALCULATOR_CABINS]
    }

    const cabins = getVisibleMaldivesRewardsPayCalculatorCabins({
      tierPricingByCabin,
      cabins: MALDIVES_REWARDS_PAY_CALCULATOR_CABINS,
      balancePP,
      offers,
      applyBalance,
    })

    return cabins.length > 0 ? cabins : [...MALDIVES_REWARDS_PAY_CALCULATOR_CABINS]
  }, [isCalculatorReady, tierPricingByCabin, balancePP, offers, applyBalance])

  useEffect(() => {
    if (!visibleCabins.includes(selectedCabin)) {
      setSelectedCabin(visibleCabins[0] ?? FlightsClassTypes.ECONOMY)
    }
  }, [visibleCabins, selectedCabin])

  const cabinLabels = useMemo(() => ({
    [FlightsClassTypes.ECONOMY]: intl.formatMessage({
      id: 'M36sKYhHGLPokHmA2mCZ',
      defaultMessage: 'Economy',
      description: 'Economy cabin label in the Société Rewards pay calculator segmented control',
    }),
    [FlightsClassTypes.PREMIUM]: intl.formatMessage({
      id: 'QDMFEX9NV1XJQpp81s_J',
      defaultMessage: 'Economy Plus',
      description: 'Economy Plus cabin label in the Société Rewards pay calculator segmented control',
    }),
    [FlightsClassTypes.LUXROW]: intl.formatMessage({
      id: 'hLbSnma1RSvHpOqMjS5r',
      defaultMessage: 'Economy Extra',
      description: 'Economy Extra cabin label in the Société Rewards pay calculator segmented control',
    }),
    [FlightsClassTypes.BUSINESS]: intl.formatMessage({
      id: 'GKi-BStunZbipsPIZSF5',
      defaultMessage: 'Business',
      description: 'Business cabin label in the Société Rewards pay calculator segmented control',
    }),
  }), [intl])

  const eyebrow = intl.formatMessage({
    id: '2wlBtaPLXrCeTCwf-B6S',
    defaultMessage: 'Maldives · 2 adults · 5 nights · per person',
    description: 'Eyebrow text above the Société Rewards pay calculator section title',
  })
  const title = intl.formatMessage({
    id: 'uaAYEY03OToZBbK9m3Mn',
    defaultMessage: 'What do I need to pay?',
    description: 'Section title for the Société Rewards pay calculator on the rewards-seats-maldives landing page',
  })
  const description = intl.formatMessage({
    id: 'vdAQltYNb8YA9CyzcpFQ',
    defaultMessage: 'Watch your points turn into business class. Switch cabin to see how far Société Rewards can take you.',
    description: 'Introductory description for the Société Rewards pay calculator section',
  })
  const packageDescription = intl.formatMessage({
    id: 'CFUyECIXtVx36cBgXxSr',
    defaultMessage: 'Return flights & 5 night stay',
    description: 'Package description shown in the Société Rewards pay calculator card header',
  })
  const disclaimer = intl.formatMessage({
    id: 'p-EJXFHzBa6jvp8WOSoR',
    defaultMessage: 'Points and prices are based on LuxPlus+ member rates, accurate at time of publish and subject to change.',
    description: 'Disclaimer below the Société Rewards pay calculator on the rewards-seats-maldives landing page',
  })

  const balanceLabel = (
    <FormattedMessage
      id="bEkQSlvYcCUnHk_Puo5d"
      defaultMessage="Your balance: {points}"
      description="Read-only balance label on the Société Rewards pay calculator control row, with formatted points total"
      values={{
        points: <FormatLoyaltyPoints points={balancePP} abbreviated />,
      }}
    />
  )

  const loggedOutBalanceLabel = (
    <FormattedMessage
      id="zvYXdwSTxQ2ux0hh_zmo"
      defaultMessage="Your balance: <link>Log in to view</link>"
      description="Balance chip label when logged out on the Société Rewards pay calculator; link opens login modal"
      values={{
        link: (chunks: React.ReactNode) => (
          <TextLink key="login-link" weight="bold" onClick={handleLoginClick}>
            {chunks}
          </TextLink>
        ),
      }}
    />
  )

  const enrollBalanceLabel = (
    <FormattedMessage
      id="Ax4ZImi7cdxVsKKMxCL6"
      defaultMessage="Your balance: <link>Activate membership</link>"
      description="Balance chip label when logged in without Société membership on the pay calculator; link opens join modal"
      values={{
        link: (chunks: React.ReactNode) => (
          <TextLink key="enroll-link" weight="bold" onClick={handleEnrollClick}>
            {chunks}
          </TextLink>
        ),
      }}
    />
  )

  const balanceChipContent = balanceFetching ? (
    <Group direction="horizontal" gap={8} verticalAlign="center">
      <LoadingBox height={24} width={24} borderRadius="M" />
      <TextLoadingBox width="14ch" typography="caption-large" />
    </Group>
  ) : !isLoggedIn ? (
    <CaptionTextBlock
      variant="large"
      weight="bold"
      startIcon={<LoyaltyTierIcon size="S" tier={displayTier} />}
    >
      {loggedOutBalanceLabel}
    </CaptionTextBlock>
  ) : needsEnroll ? (
    <CaptionTextBlock
      variant="large"
      weight="bold"
      startIcon={<LoyaltyTierIcon size="S" tier={displayTier} />}
    >
      {enrollBalanceLabel}
    </CaptionTextBlock>
  ) : wrongRegion ? (
    <CaptionTextBlock
      variant="large"
      weight="bold"
      startIcon={<LoyaltyTierIcon size="S" tier={displayTier} />}
    >
      <LoyaltySwitchRegionPrompt
        loyaltyCurrencyLabel={loyaltyCurrencyLabel}
        switchRegionPath={switchRegionPath}
        onSwitchRegion={handleSwitchRegion}
      />
    </CaptionTextBlock>
  ) : (
    <CaptionTextBlock
      variant="large"
      weight="bold"
      startIcon={<LoyaltyTierIcon size="S" tier={displayTier} />}
    >
      {balanceLabel}
    </CaptionTextBlock>
  )

  const selectedTier = tierPricingByCabin[selectedCabin]

  const calculatorContent = !isCalculatorReady ? (
    <LoadingBox height={320} borderRadius="L" />
  ) : selectedTier ? (
    <LoyaltyRewardsPayCalculatorCard
      cabinLabel={cabinLabels[selectedCabin]}
      packageDescription={packageDescription}
      tier={selectedTier}
      memberTier={displayTier}
      balancePP={balancePP}
      isLoggedIn={isLoggedIn}
      needsEnroll={needsEnroll}
      activateMembershipLabel={activateMembershipLabel}
      onEnrollClick={handleEnrollClick}
      wrongRegion={wrongRegion}
      loyaltyCurrencyLabel={loyaltyCurrencyLabel}
      switchRegionPath={switchRegionPath}
      onSwitchRegion={handleSwitchRegion}
      offers={offers}
      applyBalance={applyBalance}
      offersFetching={offersFetching}
    />
  ) : (
    <LoadingBox height={320} borderRadius="L" />
  )

  return (
    <Pane padding="48 0" background="neutral-eight">
      <LayoutContainer variant="medium">
        <Group direction="vertical" gap={24}>
          <Group direction="vertical" gap={8}>
            <Caption variant="medium" weight="bold" colour="highlight-primary-dark" align="center">
              {eyebrow}
            </Caption>
            <Heading variant="heading2" as="h2" align="center">{title}</Heading>
            <BodyText variant="medium" align="center">{description}</BodyText>
          </Group>

          <ControlRow>
            <SegmentedOptionsGroup<MaldivesRewardsPayCalculatorCabin>
              inputsName="maldives-rewards-pay-calculator-cabin"
              onValueChange={onCabinChange}
              distribution="hugged"
            >
              {visibleCabins.map(cabin => (
                <SegmentedOption
                  key={cabin}
                  value={cabin}
                  title={cabinLabels[cabin]}
                  checked={selectedCabin === cabin}
                />
              ))}
            </SegmentedOptionsGroup>

            {applyBalance && (
              <BalanceChip>
                {balanceChipContent}
              </BalanceChip>
            )}
          </ControlRow>

          {calculatorContent}

          <Caption variant="medium" align="center">{disclaimer}</Caption>
        </Group>
      </LayoutContainer>
    </Pane>
  )
}

export default LoyaltyRewardsPayCalculatorSection
